import { useQuery } from '@tanstack/react-query';
import type { ArticleFilters } from '../services/api';
import {
  getNews,
  getNewsByCategory,
  getDashboardSummary,
  getArticleById,
  getLastRun,
} from '../services/news.api';

/**
 * Fetch paginated news articles with filters.
 */
export function useNews(filters: ArticleFilters = {}) {
  return useQuery({
    queryKey: ['news', filters],
    queryFn: () => getNews(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Fetch paginated news articles for a specific category.
 */
export function useNewsByCategory(slug: string, filters: ArticleFilters = {}) {
  return useQuery({
    queryKey: ['news', 'category', slug, filters],
    queryFn: () => getNewsByCategory(slug, filters),
    staleTime: 2 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    enabled: !!slug,
  });
}

/**
 * Fetch dashboard summary data.
 */
export function useDashboardSummary() {
  return useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: getDashboardSummary,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });
}

/**
 * Fetch a single article by ID.
 */
export function useArticle(id: number) {
  return useQuery({
    queryKey: ['article', id],
    queryFn: () => getArticleById(id),
    staleTime: 10 * 60 * 1000,
    enabled: !!id,
  });
}

/**
 * Fetch the latest aggregation run info for the dashboard.
 */
export function useLastRun() {
  return useQuery({
    queryKey: ['dashboard', 'lastRun'],
    queryFn: getLastRun,
    staleTime: 5 * 60 * 1000,
  });
}
